import { ChangeEvent } from 'react'
import { Label, Textarea, Button, Alert, AlertDescription } from '@creanizer/ui'
import { Upload, Info } from 'lucide-react'
import type { CommunityInput, SavedWorldBuilding, SavedCommunity, SavedCharacter, SavedEnemy } from '@creanizer/types'
import { WorldContextForm } from './WorldContextForm'
import { CommunityContextForm } from './CommunityContextForm'
import { CharacterContextForm } from './CharacterContextForm'
import { EnemyContextForm } from './EnemyContextForm'

interface IdeaModeFormProps {
  input: CommunityInput
  onChange: (input: CommunityInput) => void
  savedWorlds?: SavedWorldBuilding[]
  savedCommunities?: SavedCommunity[]
  characters?: SavedCharacter[]
  enemies?: SavedEnemy[]
}

export function IdeaModeForm({ input, onChange, savedWorlds = [], savedCommunities = [], characters = [], enemies = [] }: IdeaModeFormProps) {
  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      onChange({
        ...input,
        ideaText: text,
      })
    }
    reader.readAsText(file)
  }
  
  return (
    <div className="space-y-4">
      {/* 世界観連携 */}
      <WorldContextForm
        input={input}
        onChange={onChange}
        savedWorlds={savedWorlds}
      />
      
      {/* 関連コミュニティ連携 */}
      <CommunityContextForm
        input={input}
        onChange={onChange}
        savedCommunities={savedCommunities}
      />
      
      {/* 関連キャラクター連携 */}
      <CharacterContextForm
        input={input}
        onChange={onChange}
        characters={characters}
      />
      
      {/* 関連エネミー連携 */}
      <EnemyContextForm
        input={input}
        onChange={onChange}
        enemies={enemies}
      />
      
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          アイディアモードでは、入力されたテキストやファイルの内容から共同体を構築します。
          規模、タイプ、豊かさレベルなどのパラメータは内容に基づいてAIが自動的に決定します。
        </AlertDescription>
      </Alert>
      
      <div>
        <Label htmlFor="ideaText">共同体のアイディアを入力</Label>
        <Textarea
          id="ideaText"
          value={input.ideaText || ''}
          onChange={(e) => onChange({ ...input, ideaText: e.target.value })}
          placeholder="共同体のアイディアを自由に入力してください。
例：
・霧に包まれた山間の村で、住民全員が秘密の魔法使い
・地下に築かれた難民都市、地上は汚染されている
・浮遊する島々を行き来する空中交易都市
・古代遺跡の上に建てられた考古学者たちの研究都市
・深海に沈んだ都市で、住民は水中呼吸の能力を持つ"
          rows={10}
        />
      </div>
      
      <div>
        <Label>またはファイルをアップロード</Label>
        <div className="mt-2">
          <input
            id="idea-file-upload"
            type="file"
            accept=".txt,.md"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('idea-file-upload')?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            ファイルを選択
          </Button>
          {input.ideaText && (
            <p className="text-sm text-muted-foreground mt-2">
              ファイルが読み込まれました
            </p>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          テキストファイル（.txt、.md）からアイディアを読み込めます
        </p>
      </div>
    </div>
  )
}